import { CSSProperties, ReactNode } from 'react';
import { useNarrow } from '../../../lib/use-narrow';
import { CatchUpSections } from '../catch-up';
import { HomeHeader } from './HomeHeader';

export function DashboardPane({
  children = null,
  scroll = false,
  bare = false,
  home2 = false,
  map = false,
}: {
  children?: ReactNode;
  scroll?: boolean;
  // The empty home takes over the whole pane — no greeting, no catch-up band — so the
  // "One home for everything" intro sits alone, matching Figma `10941:75527` (the same
  // way the empty Your-Agents screen drops its page title).
  bare?: boolean;
  // Home 2 (symphonyHome2Web): render ONLY the header top (island + greeting + date) — no catch-up
  // band, no dashboard children. Everything after the header is stripped for the redesign.
  home2?: boolean;
  // The map home: the header renders only the top row (island, greeting, date) — the brief is a node ON
  // the map, so the header's brief row is dropped. Implies `home2`.
  map?: boolean;
}) {
  const narrow = useNarrow();
  return (
    <section
      className="orion-content-pane"
      // The MAP home is fluid: it lays itself out for any width (its own container queries, down to the
      // phone layout), so the shared 900px floor — which lets the docked chat sit OVER the page and makes
      // the reader scroll sideways — is dropped here and the page shrinks beside the chat instead.
      data-fluid={map ? 'true' : undefined}
      style={{
        ...styles.pane,
        // Narrow home: the greeting/date top-aligns with the hamburger (Figma
        // 6393-24243), so the top inset matches the hamburger's ~24px offset
        // instead of the 96px band other mobile pages use.
        paddingBlock: narrow ? '24px 40px' : '72px 40px',
        // The empty home's conveyor bleeds to the region's edges, so drop the 80px
        // page gutter `.orion-content-pane` adds — the centered intro keeps its
        // midpoint, only the full-width band reaches the edges.
        ...(bare ? { paddingInline: 0 } : null),
        overflowY: scroll ? 'auto' : 'hidden',
      }}
    >
      {/* The map home carries its own header inside `MapHome` (the greeting, date, island and the
          "N new updates" pill sit around the map), so the pane renders none. */}
      {!bare && !map && <HomeHeader home2={home2} />}
      {!bare && !home2 && <CatchUpSections />}
      {/* Home 2 renders its bento body as the pane's children (below the header), so — unlike the
          non-home2 dashboard slot — home2 DOES render children. */}
      {scroll ? <div style={styles.scrollChild}>{children}</div> : <div style={styles.fillChild}>{children}</div>}
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  pane: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 4,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollChild: { position: 'relative', width: '100%', flexShrink: 0 },
  fillChild: { flex: 1, minHeight: 0, position: 'relative' },
};
